import { useState, useCallback, useEffect, useRef } from 'react';
import { usePromptValidation } from './usePromptValidation';

export const usePromptForm = (existingPromptNames = [], maxInstructionsLength = 3000, initialData = null) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    llmModel: "",
    instructions: "",
  });
  
  const initializedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const isUpdatingFromAgent = useRef(false);
  
  useEffect(() => {
    if (initialData && !initializedRef.current) {
      setFormData(initialData);
      initializedRef.current = true;
    }
  }, [initialData]);

  // State for prompt metadata
  const [promptConfigureMetadata, setPromptConfigureMetadataState] = useState({});

  const {
    errors,
    formState,
    validateName,
    validateDescription,
    validateCategory,
    validateLlmModel,
    validateInstructions,
    validateAll,
    clearErrors,
  } = usePromptValidation(existingPromptNames, maxInstructionsLength);

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleNameChange = useCallback((value) => {
    updateFormData('name', value);
    validateName(value);
  }, [updateFormData, validateName]);

  const handleDescriptionChange = useCallback((value) => {
    updateFormData('description', value);
    validateDescription(value);
  }, [updateFormData, validateDescription]);

  const handleLlmModelChange = useCallback((value) => {
    updateFormData('llmModel', value);
    validateLlmModel(value);
  }, [updateFormData, validateLlmModel]);

  const handleCategoryChange = useCallback((value) => {
    updateFormData('category', value);
    validateCategory(value);
  }, [updateFormData, validateCategory]);

  const handleInstructionsChange = useCallback((value) => {
    updateFormData('instructions', value);
    validateInstructions(value);
  }, [updateFormData, validateInstructions]);

  // Method to prepare metadata for the agent
  const setPromptConfigureMetadata = useCallback(() => {
    const metadata = {
      selected_agent_id: "asst_Z0QBtuOp2sNNMXuii8loWzDa",
      additional_message: JSON.stringify({
        name: formData.name,
        description: formData.description,
        instructions: formData.instructions
      })
    };
    setPromptConfigureMetadataState(metadata);
    return metadata;
  }, [formData]);

  // Debounced useEffect to update metadata when formData changes (only for manual changes)
  useEffect(() => {
    // Don't update metadata if the change is coming from the agent
    if (isUpdatingFromAgent.current) {
      isUpdatingFromAgent.current = false;
      return;
    }

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for debounced update
    debounceTimeoutRef.current = setTimeout(() => {
      setPromptConfigureMetadata();
    }, 500); // 500ms debounce

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [formData, setPromptConfigureMetadata]);

  // Method to update form from conversation (similar to updateFromConversation)
  const updateFromPromptConversation = useCallback((key, value) => {
    const keyDict = {
      name: 'name',
      description: 'description',
      instructions: 'instructions',
    };
    
    const formField = keyDict[key];
    
    if (formField && formData.hasOwnProperty(formField)) {
      // Mark that this update is coming from the agent
      isUpdatingFromAgent.current = true;
      
      updateFormData(formField, value);
      
      switch (formField) {
        case 'name':
          validateName(value);
          break;
        case 'description':
          validateDescription(value);
          break;
        case 'instructions':
          validateInstructions(value);
          break;
      }
    }
  }, [formData, updateFormData, validateName, validateDescription, validateInstructions]);

  // Specific handleMetadata method for prompts
  const handlePromptMetadata = useCallback((object) => {
    const k = Object.keys(object).pop();
    const v = Object.values(object).pop();
    
    updateFromPromptConversation(k, v);
    // Don't immediately update metadata here since updateFromPromptConversation will handle it
  }, [updateFromPromptConversation]);

  const handleSubmit = useCallback(() => {
    return validateAll(formData);
  }, [validateAll, formData]);

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      description: "",
      category: "",
      llmModel: "",
      instructions: "",
    });
    setPromptConfigureMetadataState({});
    clearErrors();
  }, [clearErrors]);

  const isFormValid = useCallback(() => {
    return Object.values(errors).every(error => !error) && 
           Object.values(formData).every(value => value.trim());
  }, [errors, formData]);

  return {
    formData,
    errors,
    formState,
    promptConfigureMetadata,
    handleNameChange,
    handleDescriptionChange,
    handleCategoryChange,
    handleLlmModelChange,
    handleInstructionsChange,
    handleSubmit,
    resetForm,
    isFormValid,
    updateFormData,
    setPromptConfigureMetadata,
    updateFromPromptConversation,
    handlePromptMetadata,
  };
}; 